<template>
	<div class="docx-viewer">
		<div v-if="loading" class="docx-viewer__loading">
			<div class="docx-viewer__spinner"></div>
			<p>Загрузка документа...</p>
		</div>

		<div v-else-if="error" class="docx-viewer__error">
			<p class="docx-viewer__error-title">Не удалось открыть документ</p>
			<p class="docx-viewer__error-text">{{ error }}</p>
			<a
				v-if="!downloadDisabled"
				:href="fileUrl"
				:download="downloadName"
				class="docx-viewer__download-btn"
			>
				Скачать документ
			</a>
			<p v-else class="docx-viewer__error-text">
				Скачивание отключено преподавателем.
			</p>
		</div>

		<template v-else>
			<div class="docx-viewer__toolbar">
			<button
				type="button"
				class="docx-viewer__zoom-btn"
				:disabled="zoomPercent <= ZOOM_MIN"
				@click="changeZoom(-10)"
			>
				-
			</button>
			<span class="docx-viewer__zoom-label">{{ zoomPercent }}%</span>
			<button
				type="button"
				class="docx-viewer__zoom-btn"
				:disabled="zoomPercent >= ZOOM_MAX"
				@click="changeZoom(10)"
			>
				+
			</button>
			<button
				type="button"
				class="docx-viewer__zoom-reset"
				:disabled="zoomPercent === 100"
				@click="resetZoom"
			>
				100%
			</button>
			</div>

			<div
			class="docx-viewer__content"
			:class="{ 'docx-viewer__content--protected': downloadDisabled }"
			:style="pageZoomVars"
			aria-label="Предпросмотр документа"
			@contextmenu="preventProtectedAction"
			@copy="preventProtectedAction"
			@cut="preventProtectedAction"
			@dragstart="preventProtectedAction"
			@selectstart="preventProtectedAction"
		>
			<div v-if="downloadDisabled" class="docx-viewer__watermark" aria-hidden="true">
				Protected View
			</div>
			<div v-if="!html && !text" class="docx-viewer__empty">
				Предпросмотр недоступен (документ пустой или текст не найден).
			</div>
			<div v-else-if="html" class="docx-viewer__html docx-viewer__page" v-html="safeHtml"></div>
			<pre v-else class="docx-viewer__pre docx-viewer__page">{{ text }}</pre>
			</div>
		</template>
	</div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue'
import JSZip from 'jszip'
import api from '@/services/api'
import DOMPurify from 'dompurify'

const props = defineProps({
	fileUrl: { type: String, required: true },
	downloadName: { type: String, default: '' },
	downloadDisabled: { type: Boolean, default: false },
})

const loading = ref(false)
const error = ref('')
const text = ref('')
const html = ref('')
const ZOOM_MIN = 50
const ZOOM_MAX = 250
const zoomPercent = ref(100)

const pageZoomVars = computed(() => ({
	'--docx-zoom': String(zoomPercent.value / 100),
}))

const safeHtml = computed(() => {
	if (!html.value) return ''
	return DOMPurify.sanitize(html.value, {
		ADD_ATTR: ['style', 'target', 'rel'],
		ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|data:image\/)/i,
	})
})

function isSameOrigin(url) {
	try {
		const u = new URL(url, window.location.href)
		return u.origin === window.location.origin
	} catch (e) {
		return true
	}
}

function extractDocxPlainText(documentXml) {
	const parser = new DOMParser()
	const xml = parser.parseFromString(documentXml, 'application/xml')

	// WordprocessingML:
	// - paragraph: w:p
	// - run: w:r
	// - text: w:t
	// - line break: w:br
	// - tab: w:tab
	const paragraphs = Array.from(xml.getElementsByTagName('w:p'))
	if (!paragraphs.length) return ''

	const lines = paragraphs.map(p => {
		let line = ''
		const children = p.getElementsByTagName('*')
		for (const node of children) {
			if (!node || !node.tagName) continue
			if (node.tagName === 'w:t') {
				line += node.textContent || ''
			} else if (node.tagName === 'w:tab') {
				line += '\t'
			} else if (node.tagName === 'w:br') {
				line += '\n'
			}
		}
		return line.trimEnd()
	})

	// Убираем хвостовые пустые строки, но сохраняем структуру документа.
	while (lines.length && !lines[lines.length - 1]) lines.pop()
	return lines.join('\n')
}

function escapeHtml(value) {
	return String(value || '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/\"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

function getLocalName(tagName) {
	if (!tagName) return ''
	const str = String(tagName)
	const idx = str.indexOf(':')
	return idx >= 0 ? str.slice(idx + 1) : str
}

function firstByLocalName(root, name) {
	if (!root) return null
	const nodes = root.getElementsByTagName('*')
	for (const node of nodes) {
		if (node?.tagName && getLocalName(node.tagName) === name) return node
	}
	return null
}

function getAttrAny(node, names) {
	if (!node) return ''
	for (const n of names) {
		const v = node.getAttribute?.(n)
		if (v) return v
	}
	return ''
}

async function loadDocRels(zip) {
	// word/_rels/document.xml.rels
	const relFile = zip.file('word/_rels/document.xml.rels')
	if (!relFile) return new Map()
	const relXml = await relFile.async('text')
	const relDoc = new DOMParser().parseFromString(relXml, 'application/xml')
	const rels = new Map()
	const relNodes = relDoc.getElementsByTagName('Relationship')
	for (const r of relNodes) {
		const id = r.getAttribute('Id')
		if (!id) continue
		rels.set(id, {
			target: r.getAttribute('Target') || '',
			type: r.getAttribute('Type') || '',
			targetMode: r.getAttribute('TargetMode') || '',
		})
	}
	return rels
}

function normalizeTargetToZipPath(target) {
	// Targets in document.xml.rels are typically relative to word/
	// e.g. "media/image1.png" or "../media/image1.png"
	if (!target) return ''
	let t = String(target).replace(/\\/g, '/')
	while (t.startsWith('../')) t = t.slice(3)
	if (t.startsWith('/')) t = t.slice(1)
	if (!t.startsWith('word/')) t = `word/${t}`
	return t
}

function mimeFromPath(filePath) {
	const p = String(filePath || '').toLowerCase()
	if (p.endsWith('.png')) return 'image/png'
	if (p.endsWith('.jpg') || p.endsWith('.jpeg')) return 'image/jpeg'
	if (p.endsWith('.gif')) return 'image/gif'
	if (p.endsWith('.webp')) return 'image/webp'
	if (p.endsWith('.svg')) return 'image/svg+xml'
	return 'application/octet-stream'
}

function runStyleFromRPr(rPr) {
	if (!rPr) return ''
	let css = ''

	const has = (local) => {
		const all = rPr.getElementsByTagName('*')
		for (const n of all) {
			if (getLocalName(n.tagName) === local) return true
		}
		return false
	}

	if (has('b')) css += 'font-weight:700;'
	if (has('i')) css += 'font-style:italic;'
	if (has('u')) css += 'text-decoration:underline;'

	const colorNode = Array.from(rPr.getElementsByTagName('*')).find(n => getLocalName(n.tagName) === 'color')
	const colorVal = colorNode ? getAttrAny(colorNode, ['w:val', 'val']) : ''
	if (colorVal && colorVal !== 'auto') {
		css += `color:#${escapeHtml(colorVal)};`
	}

	const szNode = Array.from(rPr.getElementsByTagName('*')).find(n => getLocalName(n.tagName) === 'sz')
	const szVal = szNode ? getAttrAny(szNode, ['w:val', 'val']) : ''
	const halfPoints = Number(szVal)
	if (Number.isFinite(halfPoints) && halfPoints > 0) {
		// w:sz in half-points. 24 => 12pt => ~16px, keep pt for better scaling.
		css += `font-size:${halfPoints / 2}pt;`
	}

	return css
}

function paragraphHeadingLevel(p) {
	const pPr = Array.from(p.childNodes || []).find(n => n?.tagName && getLocalName(n.tagName) === 'pPr')
	if (!pPr) return 0
	const pStyle = Array.from(pPr.getElementsByTagName('*')).find(n => getLocalName(n.tagName) === 'pStyle')
	const styleVal = pStyle ? getAttrAny(pStyle, ['w:val', 'val']) : ''
	if (!styleVal) return 0
	const m = String(styleVal).match(/heading\\s*([1-6])|Heading([1-6])/i)
	const lvl = m ? Number(m[1] || m[2]) : 0
	return Number.isFinite(lvl) ? lvl : 0
}

function paragraphListInfo(p, numbering) {
	const pPr = Array.from(p.childNodes || []).find(n => n?.tagName && getLocalName(n.tagName) === 'pPr')
	if (!pPr) return null
	const numPr = Array.from(pPr.childNodes || []).find(n => n?.tagName && getLocalName(n.tagName) === 'numPr')
	if (!numPr) return null
	const numIdNode = Array.from(numPr.getElementsByTagName('*')).find(n => getLocalName(n.tagName) === 'numId')
	const ilvlNode = Array.from(numPr.getElementsByTagName('*')).find(n => getLocalName(n.tagName) === 'ilvl')
	const numId = numIdNode ? getAttrAny(numIdNode, ['w:val', 'val']) : ''
	const ilvl = ilvlNode ? getAttrAny(ilvlNode, ['w:val', 'val']) : ''
	if (!numId) return null
	const level = Number(ilvl || 0)
	const type = numbering.get(`${numId}:${level}`) || 'ul'
	return { numId: String(numId), level: Number.isFinite(level) ? level : 0, type }
}

async function loadNumberingMap(zip) {
	// Map key: `${numId}:${ilvl}` -> 'ul'|'ol'
	const file = zip.file('word/numbering.xml')
	if (!file) return new Map()
	const xmlText = await file.async('text')
	const doc = new DOMParser().parseFromString(xmlText, 'application/xml')

	const abstractById = new Map()
	for (const abs of Array.from(doc.getElementsByTagName('*')).filter(n => getLocalName(n.tagName) === 'abstractNum')) {
		const absId = getAttrAny(abs, ['w:abstractNumId', 'abstractNumId'])
		if (!absId) continue
		abstractById.set(String(absId), abs)
	}

	const numToAbstract = new Map()
	for (const num of Array.from(doc.getElementsByTagName('*')).filter(n => getLocalName(n.tagName) === 'num')) {
		const numId = getAttrAny(num, ['w:numId', 'numId'])
		const absRef = Array.from(num.getElementsByTagName('*')).find(n => getLocalName(n.tagName) === 'abstractNumId')
		const absId = absRef ? getAttrAny(absRef, ['w:val', 'val']) : ''
		if (numId && absId) numToAbstract.set(String(numId), String(absId))
	}

	const result = new Map()
	for (const [numId, absId] of numToAbstract.entries()) {
		const abs = abstractById.get(absId)
		if (!abs) continue
		const levels = Array.from(abs.getElementsByTagName('*')).filter(n => getLocalName(n.tagName) === 'lvl')
		for (const lvl of levels) {
			const ilvl = getAttrAny(lvl, ['w:ilvl', 'ilvl'])
			const fmtNode = Array.from(lvl.getElementsByTagName('*')).find(n => getLocalName(n.tagName) === 'numFmt')
			const fmt = fmtNode ? getAttrAny(fmtNode, ['w:val', 'val']) : ''
			const type = String(fmt).toLowerCase() === 'bullet' ? 'ul' : 'ol'
			result.set(`${numId}:${Number(ilvl || 0)}`, type)
		}
	}
	return result
}

async function renderDocxToHtml(zip, docXmlText) {
	const doc = new DOMParser().parseFromString(docXmlText, 'application/xml')
	const body = firstByLocalName(doc, 'body')
	if (!body) return ''

	const rels = await loadDocRels(zip)
	const numbering = await loadNumberingMap(zip)
	const imageCache = new Map() // rId -> dataUrl

	const getImageDataUrl = async (rId) => {
		if (!rId) return ''
		if (imageCache.has(rId)) return imageCache.get(rId)
		const rel = rels.get(rId)
		if (!rel?.target) return ''
		const zipPath = normalizeTargetToZipPath(rel.target)
		const file = zip.file(zipPath)
		if (!file) return ''
		const base64 = await file.async('base64')
		const mime = mimeFromPath(zipPath)
		const dataUrl = `data:${mime};base64,${base64}`
		imageCache.set(rId, dataUrl)
		return dataUrl
	}

	const renderRunsToHtml = async (containerNode) => {
		let out = ''

		const nodes = Array.from(containerNode.childNodes || []).filter(n => n.nodeType === 1)
		for (const node of nodes) {
			const ln = getLocalName(node.tagName)

			if (ln === 'hyperlink') {
				const rId = getAttrAny(node, ['r:id', 'id'])
				const rel = rels.get(rId)
				const href = rel?.target || ''
				const inner = await renderRunsToHtml(node)
				if (href) {
					out += `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${inner}</a>`
				} else {
					out += inner
				}
				continue
			}

			if (ln !== 'r') continue

			const rPr = Array.from(node.childNodes || []).find(n => n?.tagName && getLocalName(n.tagName) === 'rPr')
			const style = runStyleFromRPr(rPr)

			let runHtml = ''
			const runChildren = Array.from(node.getElementsByTagName('*'))
			for (const child of runChildren) {
				const cln = getLocalName(child.tagName)
				if (cln === 't') {
					runHtml += escapeHtml(child.textContent || '')
				} else if (cln === 'tab') {
					runHtml += '\t'
				} else if (cln === 'br') {
					runHtml += '<br/>'
				} else if (cln === 'blip') {
					const embed = getAttrAny(child, ['r:embed', 'embed'])
					const src = await getImageDataUrl(embed)
					if (src) {
						runHtml += `<img class="docx-viewer__img" src="${src}" alt="" />`
					}
				}
			}

			if (!runHtml) continue
			out += style ? `<span style="${escapeHtml(style)}">${runHtml}</span>` : runHtml
		}

		return out
	}

	const parts = []
	const listStack = [] // [{ level, type }]

	const closeListsTo = (targetLevel) => {
		while (listStack.length - 1 > targetLevel) {
			const last = listStack.pop()
			parts.push(`</li></${last.type}>`)
		}
	}

	const closeAllLists = () => closeListsTo(-1)

	const openListAt = (type) => {
		parts.push(`<${type}><li>`)
		listStack.push({ type })
	}

	const continueListItem = () => parts.push(`</li><li>`)

	const bodyChildren = Array.from(body.childNodes || []).filter(n => n.nodeType === 1)
	for (const child of bodyChildren) {
		const ln = getLocalName(child.tagName)

		if (ln === 'p') {
			const listInfo = paragraphListInfo(child, numbering)

			if (!listInfo) {
				closeAllLists()
				const heading = paragraphHeadingLevel(child)
				const content = await renderRunsToHtml(child)
				if (!content) continue
				if (heading >= 1 && heading <= 6) {
					parts.push(`<h${heading} class="docx-viewer__h">${content}</h${heading}>`)
				} else {
					parts.push(`<p class="docx-viewer__p">${content}</p>`)
				}
				continue
			}

			const level = Math.max(0, listInfo.level)
			const type = listInfo.type

			if (listStack.length === 0) {
				openListAt(type)
			} else {
				const currentLevel = listStack.length - 1
				if (level > currentLevel) {
					// nested list
					openListAt(type)
				} else if (level < currentLevel) {
					closeListsTo(level)
					// switch list type if needed
					const currentType = listStack[listStack.length - 1]?.type
					if (currentType !== type) {
						const last = listStack.pop()
						parts.push(`</li></${last.type}><${type}><li>`)
						listStack.push({ type })
					} else {
						continueListItem()
					}
				} else {
					const currentType = listStack[listStack.length - 1]?.type
					if (currentType !== type) {
						const last = listStack.pop()
						parts.push(`</li></${last.type}><${type}><li>`)
						listStack.push({ type })
					} else {
						continueListItem()
					}
				}
			}

			const content = await renderRunsToHtml(child)
			parts.push(content || '')
			continue
		}

		if (ln === 'tbl') {
			closeAllLists()
			let tableHtml = '<table class="docx-viewer__table"><tbody>'
			const rows = Array.from(child.childNodes || []).filter(n => n?.tagName && getLocalName(n.tagName) === 'tr')
			for (const tr of rows) {
				tableHtml += '<tr>'
				const cells = Array.from(tr.childNodes || []).filter(n => n?.tagName && getLocalName(n.tagName) === 'tc')
				for (const tc of cells) {
					// tc may have multiple paragraphs
					const ps = Array.from(tc.childNodes || []).filter(n => n?.tagName && getLocalName(n.tagName) === 'p')
					let cell = ''
					for (const p of ps) {
						const content = await renderRunsToHtml(p)
						if (content) cell += `<p class="docx-viewer__p">${content}</p>`
					}
					tableHtml += `<td>${cell || ''}</td>`
				}
				tableHtml += '</tr>'
			}
			tableHtml += '</tbody></table>'
			parts.push(tableHtml)
		}
	}

	closeAllLists()
	return parts.join('\n')
}

async function load() {
	const url = props.fileUrl
	if (!url) {
		text.value = ''
		html.value = ''
		error.value = 'Ссылка на файл не указана.'
		return
	}

	loading.value = true
	error.value = ''
	text.value = ''
	html.value = ''

	try {
		// Для приватных файлов нужен Authorization. Office/Google viewers его не отправляют,
		// поэтому тянем файл сами и делаем простой предпросмотр (текст).
		const arrayBuffer = await api.get(url, {
			baseURL: '',
			responseType: 'arraybuffer',
			// Если ссылка ведёт на внешний домен, токен не подставляем.
			skipAuth: !isSameOrigin(url),
		})

		const zip = await JSZip.loadAsync(arrayBuffer)
		const docFile = zip.file('word/document.xml')
		if (!docFile) {
			throw new Error('В файле .docx не найден word/document.xml')
		}
		const xmlText = await docFile.async('text')
		text.value = extractDocxPlainText(xmlText)
		html.value = await renderDocxToHtml(zip, xmlText)
	} catch (e) {
		error.value = e?.message || 'Ошибка загрузки/разбора .docx'
	} finally {
		loading.value = false
	}
}

function clampZoom(value) {
	return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value))
}

function changeZoom(delta) {
	zoomPercent.value = clampZoom(zoomPercent.value + delta)
}

function resetZoom() {
	zoomPercent.value = 100
}

function preventProtectedAction(event) {
	if (!props.downloadDisabled) return
	event.preventDefault()
	event.stopPropagation()
}

function handleProtectedKeydown(event) {
	if (!props.downloadDisabled) return
	const key = String(event.key || '').toLowerCase()
	const ctrlOrMeta = event.ctrlKey || event.metaKey
	const blockedShortcuts = ['c', 's', 'p', 'u']
	const isBlockedShortcut = ctrlOrMeta && blockedShortcuts.includes(key)
	const isPrintScreen = event.key === 'PrintScreen'
	if (!isBlockedShortcut && !isPrintScreen) return

	event.preventDefault()
	event.stopPropagation()

	if (isPrintScreen && navigator?.clipboard?.writeText) {
		navigator.clipboard.writeText('').catch(() => {})
	}
}

onMounted(() => {
	window.addEventListener('keydown', handleProtectedKeydown, true)
})

onBeforeUnmount(() => {
	window.removeEventListener('keydown', handleProtectedKeydown, true)
})

watch(
	() => props.fileUrl,
	() => {
		resetZoom()
		load()
	},
	{ immediate: true }
)
</script>

<style scoped>
.docx-viewer {
	width: 100%;
	min-height: 60vh;
	display: flex;
	flex-direction: column;
	background: #ffffff;
	border-radius: 16px;
	box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
	overflow: hidden;
}

.docx-viewer__loading,
.docx-viewer__error {
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 14px;
	padding: 50px 30px;
	text-align: center;
}

.docx-viewer__spinner {
	width: 44px;
	height: 44px;
	border: 4px solid #e2e8f0;
	border-top-color: #2563eb;
	border-radius: 50%;
	animation: spin 1s linear infinite;
}

@keyframes spin {
	to { transform: rotate(360deg); }
}

.docx-viewer__error {
	background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
	border: 2px solid #f87171;
	margin: 18px;
	border-radius: 16px;
}

.docx-viewer__error-title {
	font-size: 18px;
	font-weight: 700;
	color: #b91c1c;
	margin: 0;
}

.docx-viewer__error-text {
	font-size: 14px;
	color: #7f1d1d;
	margin: 0;
	max-width: 640px;
	line-height: 1.6;
}

.docx-viewer__download-btn {
	background: linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%);
	color: #ffffff;
	padding: 12px 20px;
	border-radius: 10px;
	font-size: 14px;
	font-weight: 700;
	text-decoration: none;
}

.docx-viewer__toolbar {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 14px;
	border-bottom: 1px solid #e2e8f0;
	background: #f8fafc;
}

.docx-viewer__zoom-btn,
.docx-viewer__zoom-reset {
	border: 1px solid #cbd5e1;
	background: #ffffff;
	color: #0f172a;
	border-radius: 8px;
	height: 30px;
	padding: 0 10px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
}

.docx-viewer__zoom-btn:disabled,
.docx-viewer__zoom-reset:disabled {
	opacity: 0.45;
	cursor: not-allowed;
}

.docx-viewer__zoom-label {
	min-width: 52px;
	text-align: center;
	font-size: 13px;
	font-weight: 700;
	color: #0f172a;
}

.docx-viewer__content {
	position: relative;
	flex: 1;
	padding: 20px 22px;
	overflow: auto;
	background: #e2e8f0;
}

.docx-viewer__content--protected {
	user-select: none;
	-webkit-user-select: none;
}

.docx-viewer__empty {
	color: #64748b;
	font-size: 14px;
}

.docx-viewer__page {
	zoom: var(--docx-zoom);
}

.docx-viewer__pre {
	max-width: 816px;
	margin: 0 auto;
	padding: 52px 58px;
	white-space: pre-wrap;
	word-break: break-word;
	font-family: ui-serif, Georgia, 'Times New Roman', Times, serif;
	font-size: 16px;
	line-height: 1.7;
	color: #0f172a;
	background: #ffffff;
	border: 1px solid #e2e8f0;
	box-shadow: 0 10px 28px rgba(15, 23, 42, 0.14);
}

.docx-viewer__watermark {
	position: absolute;
	inset: 0;
	pointer-events: none;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: clamp(20px, 3vw, 42px);
	font-weight: 700;
	letter-spacing: 0.15em;
	text-transform: uppercase;
	color: rgba(15, 23, 42, 0.08);
	z-index: 1;
}

@supports not (zoom: 1) {
	.docx-viewer__page {
		transform: scale(var(--docx-zoom));
		transform-origin: top center;
		width: calc(100% / var(--docx-zoom));
	}
}

.docx-viewer__html {
	max-width: 816px;
	margin: 0 auto;
	padding: 52px 58px;
	font-family: ui-serif, Georgia, 'Times New Roman', Times, serif;
	font-size: 16px;
	line-height: 1.7;
	color: #0f172a;
	background: #ffffff;
	border: 1px solid #e2e8f0;
	box-shadow: 0 10px 28px rgba(15, 23, 42, 0.14);
}

.docx-viewer__p {
	margin: 0 0 12px 0;
}

.docx-viewer__h {
	margin: 18px 0 12px 0;
	font-weight: 700;
}

.docx-viewer__table {
	width: 100%;
	border-collapse: collapse;
	margin: 14px 0;
}

.docx-viewer__table td {
	border: 1px solid #e2e8f0;
	padding: 10px 12px;
	vertical-align: top;
}

.docx-viewer__img {
	max-width: 100%;
	height: auto;
	display: inline-block;
	margin: 10px 0;
}

@media (max-width: 900px) {
	.docx-viewer__content {
		padding: 12px;
	}

	.docx-viewer__pre,
	.docx-viewer__html {
		padding: 20px 18px;
	}
}
</style>
